<template>
  <div v-loading.fullscreen="loading" class="flex flex-col h-full">
    <div>
      <div class="full-page-width fixed-header-container">
        <div class="flex items-center pb-8 border-solid border-b-1 border-gray-300 header-container">
          <div class="flex-grow items-center flex header-container__left">
            <PageHeader :title="$t('barcode.search')" :isHideRefresh="true" />
          </div>
          <div v-if="referenceAvailable && !isLoggedAsClient" class="flex header-container__right mr-3">
            <el-button @click="onRefreshReferenceFieldClick" type="primary" size="medium">
              <i class="el-icon-refresh"></i>
            </el-button>
          </div>
          <div
            v-if="barcodeReportTemplates && barcodeReportTemplates.length > 0 && !isLoggedAsClient"
            class="flex header-container__right mr-3"
          >
            <div class="mr-3" v-if="isAllowLinking">
              <el-dropdown placement="bottom-end">
                <el-button class="rounded-l-md" type="default" size="medium">
                  {{ $t('actions') }}
                  <el-icon class="el-icon-arrow-down"></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="onOpenSelectLinkingTypeDialog()">
                      <div class="flex flex-row items-center">
                        <span>{{ $t('barcode.link_barcode') }}</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>

            <el-popover
              v-if="isShowReportOutput"
              placement="bottom-end"
              :width="200"
              trigger="hover"
              popper-class="popover-barcode-list"
            >
              <template #reference>
                <el-button type="primary" size="medium" class="text-white focus:text-white" :disabled="loading">
                  {{ $t('barcode_type.report_output') }}
                </el-button>
              </template>
              <div
                v-for="reportTemplate in barcodeReportTemplates"
                :key="reportTemplate.code"
                role="option"
                :class="['cursor-pointer text-center', 'text-black', 'my-2']"
                @click="requestReport(reportTemplate.code || '')"
              >
                {{ reportTemplate.buttonLabel }}
              </div>
            </el-popover>
          </div>
        </div>
      </div>
      <div class="pt-12">
        <div class="barcode-detail-container">
          <div class="barcode-detail-container__left">
            <div class="relative mt-8 pt-6">
              <div class="absolute top-4 right-0 w-36" v-if="dataUrl">
                <img :src="dataUrl" class="w-full" />
              </div>
              <div class="">
                <div class="font-medium h-11 my-4px flex items-center">
                  {{ $t('barcode.overview') }}
                </div>
                <div class="flex mx--2 my-4px">
                  <div class="w-1/2 px-2px">
                    <BarcodeRow :labelWidth="40" :label="'ID'" :value="barcode.id" :isCoppy="true" />
                  </div>
                  <div class="w-full--50-percent-40 px-2px">
                    <BarcodeRow
                      :labelWidth="24"
                      :label="$t('status')"
                      :value="barcode.isActive ? $t('active') : $t('inactive')"
                    />
                  </div>
                  <div class="w-40 px-2px"></div>
                </div>
                <div class="flex mx--2 my-4px">
                  <div class="w-full--40 px-2px">
                    <div class="flex mx--2">
                      <div class="px-2px" :class="`w-40`">
                        <div class="h-10 text-gray-500 bg-gray-100 flex items-center px-2">
                          {{ 'URL' }}
                        </div>
                      </div>
                      <div class="px-2px w-full--40">
                        <div class="flex h-10 bg-white items-center px-2">
                          <template v-if="barcodeURL">
                            <a class="url-underline text-black w-full truncate leading-normal" :href="barcodeURL">
                              {{ barcodeURL }}
                            </a>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="w-40 px-2px"></div>
                </div>
              </div>
            </div>
            <div class="pt-6">
              <div class="">
                <div class="font-medium h-11 my-4px flex items-center">
                  {{ $t('barcode.activationInformation') }}
                </div>
                <div>
                  <div class="flex mx--2 my-4px">
                    <div class="w-1/2 px-2px">
                      <BarcodeRow
                        :labelWidth="40"
                        :label="$t('tracking_number')"
                        :value="activationData?.trackingNumber"
                        :isCoppy="true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="mo" class="mt-6">
              <ManufacturingOrderTable :key="orderNumberMO" :mo="mo" />
            </div>
            <SerialTrackingDatas :key="barcode.id" :serialTrackingDatas="serialTrackingData" />

            <div v-if="mo" class="pt-6">
              <span class="font-medium h-11 my-4px flex items-center">{{ $t('component_item') }}</span>
              <div class="mb-6 flex-grow">
                <div class="table-wrapper">
                  <el-table
                    class="rounded-xl w-full"
                    ref="tableUsePlansRef"
                    :data="mo.usePlans || []"
                    header-cell-class-name="custom-table__header-cell"
                    cell-class-name="custom-table__cell "
                    border
                    :empty-text="$t('empty')"
                    max-height="380px"
                  >
                    <el-table-column
                      v-for="col in componentItemColumns"
                      :key="col.prop"
                      :prop="col.prop"
                      :label="col.label"
                      :min-width="20"
                    >
                      <template v-slot="scope">
                        <span v-if="col.formatter">{{ col.formatter(scope.row) }}</span>
                        <span v-else>{{ getNestedValue(scope.row, col.prop) }}</span>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </div>
          </div>
          <div class="barcode-detail-container__right">
            <div class="mt-8 pt-6 pb-16 2xl:px-4">
              <div class="">
                <div class="font-medium h-11 my-4px flex items-center">
                  {{ $t('barcode.trackingInformation') }}
                </div>
                <div class="max-w-xl m-auto" v-if="hasFirstLinkingBarcodes || hasTrackingData">
                  <div class="border-2 border-gray-400 border-solid px-6 pt-8 pb-6 rounded-xl">
                    <TraceView
                      ref="tracingComponent"
                      :key="traceViewKey"
                      v-if="barcode && project"
                      :project="project"
                      :barcode="barcode"
                      :messages="messages"
                      :isAction="true"
                      @barcode:unlink="unlinkBarcode"
                      @barcode:undoTracking="undoTracking"
                      @barcode:invokeConcile="invokeConcile"
                      @barcode:getLedgerStatus="getLedgerStatus"
                      @web:redirect="redirectTo"
                    />
                  </div>
                </div>
                <template v-else>
                  <ItemNotFound :content="$t('empty')" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BarcodeChildren from '@/components/barcode/BarcodeChildren.vue'
import BarcodeCustomField from '@/components/barcode/BarcodeCustomField.vue'
import BarcodeRow from '@/components/barcode/BarcodeRow.vue'
import SerialTrackingDatas from '@/components/barcode/SerialTrackingDatas.vue'
import BarcodeIcon from '@/components/BarcodeIcon.vue'
import ItemNotFound from '@/components/common/ItemNotFound.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import ManufacturingOrderTable from '@/components/mo/ManufacturingOrderTable.vue'
import IconDownload from '@/components/svg/IconDownload.vue'
import IconUpload from '@/components/svg/IconUpload.vue'
import TraceView from '@/components/tracing.vue'
import {
  FETCH_BARCODE,
  FETCH_BARCODE_CHILDREN,
  SET_HAVE_NEW_EXPORT_RECORD,
  SET_PAGE_NOT_FOUND,
  UNDO_LINKING,
  UNDO_TRACKING,
} from '@/store/actions'
import {
  exportBarCode,
  getBarcodeLedgerStatus,
  invokeExternalAPI,
  reconcileBarcode,
  requestBarcodeReport,
  unpairBarcode,
  updateReferenceFieldBarcode,
} from '@/utils/api'
import { LOGICAL_OPERATOR_VALUE } from '@/utils/constants'
import { ECustomFieldType, EExportType, ELinkType, EUserType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { formatQuantityWithUnit, isEmpty } from '@/utils/helpers'
import { getNestedValue } from '@/utils/tableUtils'
import { displayDateTimeLocal, displayMomentAtParam } from '@/utils/timeUtils'
import {
  IBarcode,
  IBarcodeDefinitionType,
  IBarcodeReportTemplate,
  IDimension,
  IRedirectData,
  ITracingMessage,
  ITrackingData,
  IUsePlan,
} from '@/utils/types'
import { IManufacturingPlan } from '@/utils/types/model/manufacturingPlan'
import { openMessage } from '@/utils/utils'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import QRCode from 'qrcode'
import { Options, Vue } from 'vue-class-component'
import IconLink from '../svg/IconLink.vue'
import BarcodeHistoryDialog from './BarcodeHistoryDialog.vue'
import BarcodeLinkingDialog from './BarcodeLinkingDialog.vue'
import BarcodePairingDialog from './BarcodePairingDialog.vue'
@Options({
  components: {
    BarcodeRow,
    BarcodeIcon,
    BarcodeCustomField,
    BarcodePairingDialog,
    PageHeader,
    TraceView,
    BarcodeChildren,
    ItemNotFound,
    BarcodeHistoryDialog,
    IconDownload,
    IconUpload,
    BarcodeLinkingDialog,
    IconLink,
    ManufacturingOrderTable,
    SerialTrackingDatas,
  },
  name: 'BarcodeDetail',
})
export default class BarcodeDetail extends Vue {
  isShowChildrenDialog = false
  isShowHistoryBarcodeDialog = false
  dataUrl = ''
  recycleHistoryBarcodes = [] as IBarcode[]
  loading = false
  isLoadedRecycleHistory = false
  operationTypes = ''
  recycleHistorySkipItem = 0
  linkingType = ''

  displayDateTimeLocal(time: string) {
    return displayDateTimeLocal(this.$root.$i18n.locale, time)
  }

  get linkToType() {
    return ELinkType.LINK_TO
  }

  get serialTrackingData() {
    return this.barcode?.productDatas
  }

  get mo(): IManufacturingPlan | null {
    return this.barcode?.manufacturingPlan || null
  }

  get componentItemColumns() {
    return [
      { prop: 'itemCode', label: this.$t('component_item_code') as string },
      { prop: 'itemName', label: this.$t('component_item_name') as string },
      {
        prop: 'plannedUsageQuantity',
        label: this.$t('planned_usage_quantity') as string,
        formatter: (row: IUsePlan) => formatQuantityWithUnit(row.plannedUsageQuantity, row.stockUnit),
      },
    ]
  }

  get traceViewKey() {
    return `trace-${this.barcode?.id}-${this.barcode?.trackingData?.length || 0}-${this.barcode?.audit
      ?.updatedDateTime || ''}`
  }

  get linkByType() {
    return ELinkType.BE_LINKED
  }

  get recycleCount() {
    return this.barcode.recycleCount || 0
  }

  get isShowReportOutput() {
    return this.$store.getters.isShowReportOutput
  }

  openBarcodeHistoryDialog() {
    this.isShowHistoryBarcodeDialog = true
  }

  unpairingSelectedBCs: string[] = []
  onUpdateUnpairingBCs(bcList: string[]) {
    this.unpairingSelectedBCs = bcList
  }

  async performUnpairing() {
    try {
      this.loading = true
      const req = {
        isDryRun: true,
        parentBarcodeId: this.barcode.id,
        isUnpairAll: false,
        unpairChildrenIds: this.unpairingSelectedBCs,
      }
      await unpairBarcode(req)

      req.isDryRun = false
      await unpairBarcode(req)
      openMessage(this.$t('barcode.barcode_unpaired'), 'success')
    } catch (err) {
      errorHandler(err)
    } finally {
      this.unpairingSelectedBCs = []
      this.isShowChildrenDialog = false
      await this.$store
        .dispatch(FETCH_BARCODE, { id: this.id, path: this.path })
        .catch(() => this.$store.dispatch(SET_PAGE_NOT_FOUND, { item: 'barcode.barcode' }))
      this.loading = true
      await this.$store.dispatch(FETCH_BARCODE_CHILDREN, this.barcode.id).finally(() => (this.loading = false))

      this.loading = false
    }
  }

  isShowPairingBCDialog = false
  onOpenPairingDialog() {
    this.isShowPairingBCDialog = true
  }

  isShowLinkingBCDialog = false
  onOpenLinkingDialog() {
    this.isShowLinkingBCDialog = true
  }

  isShowSelectLinkingTypeBCDialog = false
  onOpenSelectLinkingTypeDialog() {
    this.isShowSelectLinkingTypeBCDialog = true
  }

  handleSelectLikingType(type: ELinkType) {
    this.linkingType = type
    this.isShowSelectLinkingTypeBCDialog = false
    this.isShowLinkingBCDialog = true
  }

  get isAllowPairing() {
    return (
      !this.barcode?.isDeactivated &&
      (this.barcodeType?.pairing?.[this.barcode.currentTrackpointKey ?? 0]?.isAvailable ?? false)
    )
  }

  get isShowBCPairing() {
    return this.$store.getters.isShowBCPairing
  }

  get isAllowLinking() {
    return (
      !this.barcode?.isDeactivated &&
      (this.barcodeType?.linking?.[this.barcode.currentTrackpointKey ?? 0]?.isAvailable ?? false)
    )
  }

  get messages(): ITracingMessage {
    return {
      trackingNumber: this.$t('tracking_number'),
      undoTrackingData: this.$t('barcode.undo_tracking_data'),
      noTrackingHistory: this.$t('barcode.no_tracking_history'),
      packageImages: this.$t('barcode.packageImages', { number: '{0}' }),
      confirmUndoTrackingData: this.$t('barcode.confirm_undo_tracking_data'),
      cancel: this.$t('cancel'),
      ok: this.$t('ok'),
      trackedDatetime: this.$t('barcode.tracked_datetime'),
      trackedUsername: this.$t('barcode.tracked_username'),
      sign: this.$t('sign'),
    } as ITracingMessage
  }

  get getExternalWebAPIButtonLabel() {
    const barcodeType: IBarcodeDefinitionType = this.barcodeType
    return barcodeType?.externalWebAPIIntegration?.buttonLabel || this.$t('export_pdf')
  }

  get externalWebAPIAvailable() {
    return !!this.barcodeType?.externalWebAPIIntegration
  }

  get referenceAvailable() {
    const bc = cloneDeep(this.barcode?.activationData?.customFields) || {}
    const isActivationDataReferenceAvailable = Object.keys(bc).some((val) => {
      if (bc?.[val]?.fieldType) return bc[val].fieldType === ECustomFieldType.REFERENCE
    })

    const trackingData = this.barcode.trackingData as ITrackingData[]
    const isTrackingDataReferenceAvailable = trackingData?.some((td) => {
      const isTrackingDataReferenceAvailable = Object.keys(td?.customFields || {}).some((key) => {
        if (td?.customFields?.[key]?.fieldType) return td?.customFields?.[key]?.fieldType === ECustomFieldType.REFERENCE
      })
      return isTrackingDataReferenceAvailable
    })

    return isActivationDataReferenceAvailable || isTrackingDataReferenceAvailable
  }

  get barcodeReportTemplates() {
    return this.barcodeType?.barcodeReportTemplates ?? ([] as IBarcodeReportTemplate[])
  }

  get id() {
    return this.$route.name === 'barcodeView' ? this.$route.params.id : ''
  }

  get orderNumberMO() {
    return this.barcode?.manufacturingPlan?.orderNumber
  }

  get path() {
    return this.$route.query?.path
  }

  get isAvailableRecycledBarcode() {
    return this.barcode.id !== this.barcode.rootId
  }

  get project() {
    return this.$store.state.barcode?.projectDetail
  }

  get barcode() {
    return this.$store.state.barcode?.barcode
  }

  get product() {
    return this.$store.state.barcode?.product
  }

  get barcodeType(): IBarcodeDefinitionType {
    return this.project?.barcodeTypes?.[this.barcode.barcodeType]
  }

  get projectBarcodeTypeKey() {
    return this.project?.barcodeTypes
  }

  get activationData() {
    return this.$store.state.barcode?.barcode?.activationData
  }

  get currentTrackPoint() {
    return this.project?.trackPoints?.[this.barcode?.currentTrackPointKey]?.name
  }

  get barcodeURL() {
    const appHost = process.env.VUE_APP_MOBILE_BASE_URL
    return this.barcode ? `${appHost}/qrcode/${this.barcode.id}` : ''
  }

  get hasFirstLinkingBarcodes(): boolean {
    return !isEmpty(this.barcode.activationData?.linkedBarcodes)
  }

  get hasTrackingData() {
    return !isEmpty(this.barcode?.serialTrackingDatas)
  }

  get loggedUser() {
    return this.$store.state.profile?.user || {}
  }

  get isLoggedAsClient() {
    return this.loggedUser.userType === EUserType.CLIENT || false
  }

  async onRefreshReferenceFieldClick() {
    try {
      this.loading = true
      await updateReferenceFieldBarcode(this.barcode.id)
      await this.reloadData()
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  async openChildrenBarcode() {
    if (this.barcode.id !== this.$store.state.barcode?.parentId) {
      this.loading = true
      await this.$store.dispatch(FETCH_BARCODE_CHILDREN, this.barcode.id).finally(() => (this.loading = false))
    }
    this.isShowChildrenDialog = true
  }

  async InvokeExternalWebAPI() {
    try {
      this.loading = true
      const response = await invokeExternalAPI(this.barcode?.id || '')
      if (!response) return

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `${displayMomentAtParam(moment())}.pdf`
      document.body.appendChild(link)
      link.click()
      openMessage(this.$t('barcode_status.completed'), 'success')
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  async requestReport(code: string) {
    try {
      this.loading = true
      if (this.barcode?.id) {
        await requestBarcodeReport(this.barcode.id, code)
      }

      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.BARCODE, isHaveNew: true })
    } catch (error) {
      errorHandler(error)
    } finally {
      this.loading = false
    }
  }

  async invokeConcile(index: number) {
    try {
      const { trackingData } = await reconcileBarcode(this.barcode?.id, index)
      const { isLedgerCertified, lastLedgerCertifiedDateTime, isLedgerRegistered } = trackingData
      this.$refs.tracingComponent.trackingDataItemsLoading[index].isLedgerCertified = isLedgerCertified

      this.$refs.tracingComponent.trackingDataItemsLoading[index].isLedgerRegistered = isLedgerRegistered

      this.$refs.tracingComponent.trackingDataItemsLoading[
        index
      ].lastLedgerCertifiedDateTime = lastLedgerCertifiedDateTime
    } catch (error) {
      errorHandler(error)
    } finally {
      this.$refs.tracingComponent.isLoading = false
    }
  }

  async unlinkBarcode(barcodeId: string) {
    this.loading = true
    try {
      await this.$store.dispatch(UNDO_LINKING, { barcodeId })
      this.loading = false
      openMessage(this.$t('unlinking_success'), 'success')
      this.goToBarcode(barcodeId)
    } catch (err) {
      errorHandler(err)
    }
  }

  async undoTracking(barcodeId: string) {
    this.loading = true
    try {
      await this.$store.dispatch(UNDO_TRACKING, { barcodeId })
      this.loading = false
      this.goToBarcode(barcodeId)
    } catch (err) {
      this.loading = false
      errorHandler(err)
    }
  }

  async getLedgerStatus(index: number) {
    try {
      const { trackingData } = await getBarcodeLedgerStatus(this.barcode?.id, index)
      const { isLedgerCertified, lastLedgerCertifiedDateTime, isLedgerRegistered } = trackingData
      this.$refs.tracingComponent.trackingDataItemsLoading[index].isLedgerCertified = isLedgerCertified

      this.$refs.tracingComponent.trackingDataItemsLoading[index].isLedgerRegistered = isLedgerRegistered

      this.$refs.tracingComponent.trackingDataItemsLoading[index].isLoading = false

      this.$refs.tracingComponent.trackingDataItemsLoading[
        index
      ].lastLedgerCertifiedDateTime = lastLedgerCertifiedDateTime
    } catch (error) {
      errorHandler(error)
    } finally {
      this.$refs.tracingComponent.isLoading = false
    }
  }

  closeChildrenDialog() {
    this.isShowChildrenDialog = false
  }

  formatDimension(dimension: IDimension) {
    if (dimension) {
      const arrString = []
      if (dimension.width) arrString.push(`${this.$t('width')} ${dimension.width} cm `)
      if (dimension.depth) arrString.push(`${this.$t('depth')} ${dimension.depth} cm `)
      if (dimension.height) arrString.push(`${this.$t('height')} ${dimension.height} cm `)
      if (dimension.weight) arrString.push(`${this.$t('weight')} ${dimension.weight} kg `)
      return arrString.join(' | ')
    }

    return ''
  }

  goToBarcode(id: string) {
    this.$router.push({ name: 'barcodeView', params: { id } })
  }

  getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return getNestedValue(obj, path)
  }

  async reloadData() {
    if (!this.id) return
    try {
      await this.$store
        .dispatch(FETCH_BARCODE, { id: this.id, path: this.path })
        .catch(() => this.$store.dispatch(SET_PAGE_NOT_FOUND, { item: 'barcode.barcode' }))
      this.dataUrl = await QRCode.toDataURL(this.barcodeURL)
    } catch (error) {
      errorHandler(error as Error)
    }
  }

  async onHistorySkipItem(skip: number) {
    this.recycleHistorySkipItem = skip
    this.loading = true
    await this.loadRecycleHistory()
    this.loading = false
  }

  async created() {
    try {
      this.loading = true
      await this.reloadData()
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false
    }
  }

  redirectTo(data: IRedirectData) {
    if (data.path) {
      this.$router.push({
        name: 'barcodeView',
        params: {
          id: data.mainBarcodeId,
        },
        query: {
          path: data.path,
        },
      })
    } else {
      this.goToBarcode(data.mainBarcodeId)
    }
  }

  async downloadCSV(key: string) {
    try {
      let count = 50
      if (key === 'parentId') {
        count = this.barcode.childrenCount
      }
      if (key === 'rootId') {
        count = this.recycleHistoryBarcodes.length
      }
      const payload = {
        condition: {
          projectCode: this.project?.code,
          version: this.project?.version,
          isToday: true,
          searchConditionBlocks: [
            {
              searchConditions: [
                {
                  key: key,
                  valueType: 'string',
                  value: this.barcode?.[key] || this.barcode?.id,
                },
              ],
              logicalOperator: LOGICAL_OPERATOR_VALUE.and,
            },
          ],
          count,
          skip: 0,
        },
      }
      await exportBarCode((payload as unknown) as Record<string, unknown>)
      this.$store.commit(SET_HAVE_NEW_EXPORT_RECORD, { exportType: EExportType.BARCODE, isHaveNew: true })
    } catch (error) {
      openMessage(error as string, 'error')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

:deep() {
  .barcode-container {
    > div {
      width: 100%;
      height: 100%;
    }

    .barcode-icon {
      &__icon {
        width: 100%;
        height: 100%;
      }
    }
  }
}

.full-page-width {
  width: calc(100% - 320px);
}

.fixed-header-container {
  padding: 40px 40px 0px 40px;
  position: fixed;
  top: 0;
  left: 240px;
  z-index: 9;
  background-color: #efefef;
}

@media (max-width: #{$max-1024-width}) {
  .fixed-table {
    padding-top: 269px;

    .table-actions {
      top: 190px;
    }
  }
}

.el-process-btn {
  &.orange-btn {
    &:hover {
      border-color: lightgoldenrodyellow;
      background-color: #fb923c;
      color: white;
    }
  }

  &.green-btn {
    background-color: #20cf90;
    border-color: #a5ebd2;
    color: white;
  }

  &.is-disabled {
    color: gray;
    background-color: $light-gray !important;
  }
}
</style>
